import { mobileStrings } from "~/components/mobile/strings";
import { useInvitations } from "~/hooks/useInvitations";
import type { Client, InvitedRoom } from "~/lib/matrix";

export function MobileInvitations({ client }: { client: Client | null }) {
  const { invites, pending, error, accept, decline } = useInvitations(client);
  if (!client || invites.length === 0) return null;

  return (
    <section aria-label={mobileStrings.home.invitations} className="flex flex-col gap-2 px-4 pt-3">
      <h2 className="text-[10px] font-normal uppercase tracking-widest text-muted-quiet">
        {mobileStrings.home.invitations}
      </h2>
      <ul className="flex flex-col">
        {invites.map((invite) => (
          <InvitationRow
            key={invite.roomId}
            invite={invite}
            busy={pending.has(invite.roomId)}
            message={error?.roomId === invite.roomId ? error.message : null}
            onAccept={() => void accept(invite.roomId)}
            onDecline={() => void decline(invite.roomId)}
          />
        ))}
      </ul>
    </section>
  );
}

function InvitationRow({
  invite,
  busy,
  message,
  onAccept,
  onDecline,
}: {
  invite: InvitedRoom;
  busy: boolean;
  message: string | null;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const name = invite.name ?? (invite.isDirect ? invite.inviter : invite.roomId);
  return (
    <li className="flex min-h-11 items-center gap-3 py-2">
      <span
        aria-hidden
        className="flex size-6 shrink-0 items-center justify-center rounded-full bg-foreground/[0.1] text-base font-light text-muted-foreground"
      >
        #
      </span>
      <span className="min-w-0 flex-1">
        <span data-invitation-name={invite.roomId} className="block truncate text-base">
          {name}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {mobileStrings.home.invitedBy(invite.inviter)}
        </span>
      </span>
      <button
        type="button"
        onClick={onAccept}
        disabled={busy}
        className="flex h-11 items-center rounded-md bg-foreground px-3 text-sm font-medium text-background outline-none focus-visible:shadow-neon-cyan"
      >
        {mobileStrings.home.accept}
      </button>
      <button
        type="button"
        onClick={onDecline}
        disabled={busy}
        className="flex h-11 items-center rounded-md border-none bg-transparent px-3 text-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:shadow-neon-cyan"
      >
        {mobileStrings.home.decline}
      </button>
      {message ? (
        <span role="status" className="w-full -mt-1 pt-1 text-xs text-destructive">
          {message}
        </span>
      ) : null}
    </li>
  );
}
